import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  TextField, MenuItem, Divider, CircularProgress, Alert, Snackbar, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import { Settings, Refresh } from '@mui/icons-material';
import SubscriptionAdminService from '../../services/subscriptionAdmin.service';
import SubscriptionPlanService from '../../services/subscriptionPlan.service';
import SubscriptionService from '../../services/subscription.service';

const periodLabel = (p) =>
  p ? `${p.period_count} ${String(p.period_unit).toLowerCase()}${p.period_count > 1 ? 's' : ''}` : '';

const AdminSubscriptions = () => {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { tc, sub, payments, discounts }
  const [detailLoading, setDetailLoading] = useState(false);
  const [assign, setAssign] = useState({ planId: '', planPriceId: '' });
  const [discount, setDiscount] = useState({ discountType: 'PERCENTAGE', percentage: '', fixedAmount: '', planId: '', validFrom: '', validTo: '', notes: '', singleUse: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([SubscriptionAdminService.getSummaries(), SubscriptionPlanService.list()])
      .then(([summaries, planList]) => {
        setRows(Array.isArray(summaries) ? summaries : []);
        setPlans(Array.isArray(planList) ? planList : []);
      })
      .catch(() => notify('Failed to load subscriptions', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (row) => {
    setDetailLoading(true);
    setDetail({ tc: row });
    Promise.all([
      SubscriptionAdminService.getForOrg(row.typing_center_id),
      SubscriptionAdminService.getPayments(row.typing_center_id),
      SubscriptionAdminService.listDiscounts(row.typing_center_id),
    ])
      .then(([sub, payments, discounts]) => setDetail({ tc: row, sub, payments: payments || [], discounts: discounts || [] }))
      .catch(() => notify('Failed to load detail', 'error'))
      .finally(() => setDetailLoading(false));
  };

  const refreshDetail = () => detail && openDetail(detail.tc);

  const doAssignFree = () => {
    if (!assign.planId) { notify('Pick a plan', 'error'); return; }
    SubscriptionAdminService.assignPlan(detail.tc.typing_center_id, {
      plan_id: assign.planId,
      plan_price_id: assign.planPriceId || null,
      free: true,
    })
      .then(() => { notify('Free plan granted'); setAssign({ planId: '', planPriceId: '' }); refreshDetail(); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Assign failed', 'error'));
  };

  const selectedAssignPlan = plans.find((p) => p.id === assign.planId);

  const doCreateDiscount = () => {
    const body = {
      discount_type: discount.discountType,
      percentage: discount.discountType === 'PERCENTAGE' ? Number(discount.percentage) || 0 : null,
      fixed_amount: discount.discountType === 'FIXED' ? Number(discount.fixedAmount) || 0 : null,
      plan_id: discount.planId || null,
      valid_from: discount.validFrom || null,
      valid_to: discount.validTo || null,
      notes: discount.notes || null,
      single_use: discount.singleUse,
    };
    SubscriptionAdminService.createDiscount(detail.tc.typing_center_id, body)
      .then(() => { notify('Discount added'); setDiscount({ discountType: 'PERCENTAGE', percentage: '', fixedAmount: '', planId: '', validFrom: '', validTo: '', notes: '', singleUse: false }); refreshDetail(); })
      .catch((e) => notify(e?.response?.data?.message || 'Could not add discount', 'error'));
  };

  const deleteDiscount = (id) => {
    SubscriptionAdminService.deleteDiscount(id)
      .then(() => { notify('Discount removed'); refreshDetail(); })
      .catch(() => notify('Could not remove discount', 'error'));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Subscriptions</Typography>
          <Typography variant="caption" color="text.secondary">Per typing center: plan, status, discounts and billing.</Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Typing Center</TableCell><TableCell>Current plan</TableCell>
                  <TableCell>Status</TableCell><TableCell>Expiry</TableCell><TableCell>Notes</TableCell>
                  <TableCell align="right">Manage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.typing_center_id} hover>
                    <TableCell>{r.typing_center_name}</TableCell>
                    <TableCell>{r.current_plan_name}</TableCell>
                    <TableCell><Chip size="small" label={SubscriptionService.getStatusLabel(r.status)} color={SubscriptionService.getStatusColor(r.status)} /></TableCell>
                    <TableCell>{r.current_period_end || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.expired_plan_name ? `Lapsed: ${r.expired_plan_name} on ${r.expired_on}` : (r.ever_purchased ? '' : 'Not purchased yet')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right"><IconButton size="small" onClick={() => openDetail(r)}><Settings fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.tc?.typing_center_name}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detail?.sub ? (
            <>
              <Typography variant="subtitle2" gutterBottom>Current subscription</Typography>
              <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                <Typography variant="body2">Plan: <b>{detail.sub.plan_name || 'Free'}</b></Typography>
                <Typography variant="body2">Status: {SubscriptionService.getStatusLabel(detail.sub.status)}</Typography>
                <Typography variant="body2">Period end: {detail.sub.period_end || '—'}</Typography>
                <Typography variant="body2">Auto-renew: {detail.sub.auto_renew ? 'On' : 'Off'}</Typography>
              </Stack>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>Grant a free plan</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
                <TextField select size="small" label="Plan" value={assign.planId} sx={{ minWidth: 180 }}
                  onChange={(e) => setAssign({ planId: e.target.value, planPriceId: '' })}>
                  {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Duration (optional)" value={assign.planPriceId} sx={{ minWidth: 210 }}
                  disabled={!selectedAssignPlan}
                  onChange={(e) => setAssign({ ...assign, planPriceId: e.target.value })}>
                  <MenuItem value="">Open-ended (no expiry)</MenuItem>
                  {(selectedAssignPlan?.prices || []).map((pr) => (
                    <MenuItem key={pr.id} value={pr.id}>{periodLabel(pr)} ({pr.price} {pr.currency})</MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={doAssignFree}>Grant free</Button>
              </Stack>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>Discounts</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap">
                <TextField select size="small" label="Type" value={discount.discountType} sx={{ minWidth: 130 }}
                  onChange={(e) => setDiscount({ ...discount, discountType: e.target.value })}>
                  <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                  <MenuItem value="FIXED">Fixed</MenuItem>
                  <MenuItem value="FREE">Free</MenuItem>
                </TextField>
                {discount.discountType === 'PERCENTAGE' && (
                  <TextField size="small" label="%" type="number" value={discount.percentage} sx={{ width: 90 }}
                    onChange={(e) => setDiscount({ ...discount, percentage: e.target.value })} />
                )}
                {discount.discountType === 'FIXED' && (
                  <TextField size="small" label="Amount" type="number" value={discount.fixedAmount} sx={{ width: 110 }}
                    onChange={(e) => setDiscount({ ...discount, fixedAmount: e.target.value })} />
                )}
                <TextField select size="small" label="Plan (optional)" value={discount.planId} sx={{ minWidth: 160 }}
                  onChange={(e) => setDiscount({ ...discount, planId: e.target.value })}>
                  <MenuItem value="">Any plan</MenuItem>
                  {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
                <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
                  value={discount.validFrom} onChange={(e) => setDiscount({ ...discount, validFrom: e.target.value })} />
                <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
                  value={discount.validTo} onChange={(e) => setDiscount({ ...discount, validTo: e.target.value })} />
                <TextField size="small" label="Notes" sx={{ minWidth: 160 }}
                  value={discount.notes} onChange={(e) => setDiscount({ ...discount, notes: e.target.value })} />
                <FormControlLabel
                  control={<Switch checked={discount.singleUse} onChange={(e) => setDiscount({ ...discount, singleUse: e.target.checked })} />}
                  label="Single use" />
                <Button variant="outlined" onClick={doCreateDiscount}>Add</Button>
              </Stack>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {(detail.discounts || []).map((d) => (
                  <Stack key={d.id} direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" display="block">
                        {d.discount_type}{d.discount_type === 'PERCENTAGE' ? ` ${d.percentage}%` : d.discount_type === 'FIXED' ? ` ${d.fixed_amount}` : ''}
                        {d.plan_name ? ` · ${d.plan_name}` : ' · any plan'}
                        {d.single_use ? ' · single use' : ''}
                      </Typography>
                      {(d.valid_from || d.valid_to) && (
                        <Typography variant="caption" color="text.secondary" display="block">{d.valid_from || '—'} → {d.valid_to || '∞'}</Typography>
                      )}
                      {d.notes && <Typography variant="caption" color="text.secondary" display="block">{d.notes}</Typography>}
                    </Box>
                    <Button size="small" color="error" onClick={() => deleteDiscount(d.id)}>Remove</Button>
                  </Stack>
                ))}
                {(detail.discounts || []).length === 0 && <Typography variant="caption" color="text.secondary">No discounts</Typography>}
              </Stack>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>Billing history</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Date</TableCell><TableCell>Plan</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.payments || []).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.created_at ? String(p.created_at).slice(0, 10) : ''}</TableCell>
                        <TableCell>{p.plan_name}</TableCell>
                        <TableCell>{SubscriptionService.formatCurrency(p.amount, p.currency)}</TableCell>
                        <TableCell><Chip size="small" label={SubscriptionService.getStatusLabel(p.status)} color={SubscriptionService.getStatusColor(p.status)} /></TableCell>
                      </TableRow>
                    ))}
                    {(detail.payments || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No payments</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">No subscription data.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSubscriptions;
